/* ---------------------------------------------------------------------------
   Fechas: la convención chilena, y el puente con lo que manda el API.

   El API habla dos idiomas distintos y la diferencia NO es cosmética:

   - `fecha_entrega` es una FECHA DE CALENDARIO (`toDateString()` → `2026-09-03`).
     No tiene hora ni huso: es un día del almanaque, el mismo se mire desde
     donde se mire.
   - `created_at`, `generada_en`, `enviada_en` son INSTANTES en UTC
     (`toIso8601String()`, con `app.timezone = UTC`). Ahí el día SÍ depende del
     huso desde el que se lo lea.

   Mezclarlos corre la fecha un día, y en los dos sentidos: `new Date("2026-09-03")`
   se interpreta como medianoche UTC —en Chile, las 20:00 del día 2—, y quedarse
   con los diez primeros caracteres de `2026-08-26T02:30:00Z` dice "26" cuando en
   Chile todavía son las 22:30 del 25. Por eso son DOS funciones y no una con un
   parámetro: el tipo de dato de cada llamada decide cuál corresponde.
--------------------------------------------------------------------------- */

/**
 * Toda fecha que este sistema muestra es chilena.
 *
 * Los informes son documentos legales emitidos en Chile: "generada el 25" es
 * una afirmación sobre el calendario chileno, no sobre el reloj del computador
 * desde el que alguien mira la pantalla. Fijar la zona hace además que la
 * suite dé el mismo resultado en cualquier máquina.
 */
const ZONA_HORARIA = "America/Santiago";

const FORMATO_CHILENO = new Intl.DateTimeFormat("es-CL", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  timeZone: ZONA_HORARIA,
});

const FORMATO_CHILENO_CON_HORA = new Intl.DateTimeFormat("es-CL", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: ZONA_HORARIA,
});

/**
 * Fecha de CALENDARIO: `2026-09-03` → `03-09-2026`.
 *
 * Trabaja sobre el string y nunca construye un `Date`, a propósito. Un día del
 * almanaque no tiene instante asociado; convertirlo a `Date` es inventarle uno
 * —medianoche UTC— y en Chile eso lo retrocede al día anterior.
 *
 * Para un timestamp usá `formatearMomento`.
 */
export function formatearFecha(iso: string): string {
  const [anio, mes, dia] = iso.slice(0, 10).split("-");

  return `${dia}-${mes}-${anio}`;
}

/**
 * La máscara de tipeo del campo de fecha: deja SOLO dígitos y pone los guiones
 * por su cuenta. `"10/09/2026"` → `"10-09-2026"`, `"100"` → `"10-0"`.
 *
 * Acepta cualquier separador —la barra, el punto, el guion o ninguno— porque
 * los descarta a todos y se queda con los dígitos. Quien viene de otro sistema
 * teclea `10/09/2026` sin pensarlo y el campo lo escribe como corresponde,
 * sin corregirle nada a mano.
 *
 * EL SEPARADOR SE INSERTA ENTRE GRUPOS, NUNCA AL FINAL, y eso no es un
 * detalle estético: si `"10"` se convirtiera en `"10-"`, borrar ese guion con
 * la tecla de retroceso lo volvería a agregar y el cero quedaría imposible de
 * eliminar. Como está, cada retroceso saca exactamente un dígito.
 */
export function enmascararFecha(texto: string): string {
  const digitos = texto.replace(/\D/g, "").slice(0, 8);

  const dia = digitos.slice(0, 2);
  const mes = digitos.slice(2, 4);
  const anio = digitos.slice(4, 8);

  return [dia, mes, anio].filter((grupo) => grupo !== "").join("-");
}

/**
 * El camino de vuelta de la máscara: `"10-09-2026"` → `"2026-09-10"`, o `null`
 * si todavía no es una fecha.
 *
 * `null` cubre dos casos que para quien llama son el mismo —no hay fecha— pero
 * que valen la pena distinguir al leer el código: el texto está a medio
 * escribir (`"10-09-20"`), o está completo y NO EXISTE (`"31-02-2026"`).
 *
 * Lo segundo es lo que obliga a comparar los componentes de vuelta. `Date` no
 * falla con un 31 de febrero: lo DESBORDA al 3 de marzo, en silencio. Sin esta
 * comprobación, el campo aceptaría una fecha que nadie escribió. De paso cubre
 * el año `0000`, que `Date` reinterpreta como 1900.
 */
export function isoDesdeTextoChileno(texto: string): string | null {
  const partes = /^(\d{2})-(\d{2})-(\d{4})$/.exec(texto);
  if (partes === null) return null;

  const [, dia, mes, anio] = partes;
  const fecha = new Date(Number(anio), Number(mes) - 1, Number(dia));

  const sobrevivio =
    fecha.getFullYear() === Number(anio) &&
    fecha.getMonth() === Number(mes) - 1 &&
    fecha.getDate() === Number(dia);

  return sobrevivio ? `${anio}-${mes}-${dia}` : null;
}

/** `15` → `15 días hábiles`; `1` → `1 día hábil`. */
export function formatearPlazo(diasHabiles: number): string {
  return diasHabiles === 1 ? "1 día hábil" : `${diasHabiles} días hábiles`;
}

/**
 * Lo que la pantalla dice sobre la entrega de un informe.
 *
 * DICE EXACTAMENTE LO QUE DICE EL PDF: "15 días hábiles a contar de la visita
 * de inspección". El plazo es lo único que se pactó, y la visita que lo
 * dispara no está agendada ni al cotizar ni al aceptar, así que no hay día del
 * calendario que mostrar.
 *
 * ANTES decía "desde la aceptación" y era falso: aceptar no arranca el plazo.
 * Esa versión mostraba un día que el papel firmado nunca prometió.
 *
 * La fecha se consulta primero por las cotizaciones aceptadas bajo el modelo
 * anterior, que sí tienen una y ya se la comunicaron al cliente. El guion
 * queda para la que no tiene ninguna de las dos.
 *
 * Gemela de `FormatoDelInforme::entrega()` en el API: el documento en pantalla
 * y el PDF que recibe el cliente tienen que decir lo mismo.
 */
export function describirEntrega(
  fechaEntrega: string | null,
  plazoDiasHabiles: number | null,
): string {
  if (fechaEntrega !== null) return formatearFecha(fechaEntrega);
  if (plazoDiasHabiles === null) return "—";

  return `${formatearPlazo(plazoDiasHabiles)} a contar de la visita de inspección`;
}

/**
 * INSTANTE: `2026-08-26T02:30:00Z` → `25-08-2026`, el día que era en Chile.
 *
 * Acá sí hay que pasar por `Date`: el dato es un momento en UTC y la pregunta
 * —"¿qué día fue?"— solo tiene respuesta una vez elegido el huso.
 */
export function formatearMomento(iso: string): string {
  const momento = new Date(iso);
  if (Number.isNaN(momento.getTime())) return "—";

  return FORMATO_CHILENO.format(momento);
}

/**
 * INSTANTE con hora: `2026-09-25T18:40:01Z` → `25-09-2026 15:40`, la hora
 * chilena.
 *
 * Hermana de `formatearMomento`: existe porque el log de consumo de IA
 * necesita distinguir dos peticiones del mismo día, así que el día solo no
 * alcanza. Se arma con `formatToParts` y no con el string que devuelve
 * `format()` a secas: el separador entre fecha y hora que elige la plantilla
 * `es-CL` de ICU es una coma ("25-09-2026, 15:40"), y esta app nunca puntúa
 * así una fecha — con las partes sueltas el formato queda fijo sin importar
 * qué separador use la configuración regional de turno.
 */
export function formatearFechaYHora(iso: string): string {
  const momento = new Date(iso);
  if (Number.isNaN(momento.getTime())) return "—";

  const partes = Object.fromEntries(
    FORMATO_CHILENO_CON_HORA.formatToParts(momento).map((parte) => [parte.type, parte.value]),
  );

  return `${partes.day}-${partes.month}-${partes.year} ${partes.hour}:${partes.minute}`;
}

/**
 * `2026-09-03` → el `Date` de esa medianoche LOCAL, que es lo que consume el
 * calendario del selector de fecha.
 *
 * `new Date(iso)` daría medianoche UTC y el calendario abriría marcando el día
 * anterior. Con los componentes por separado el `Date` cae en el día correcto
 * sin importar el huso de quien mira.
 */
export function fechaDesdeIso(iso: string): Date | null {
  const partes = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (partes === null) return null;

  const [, anio, mes, dia] = partes;
  const fecha = new Date(Number(anio), Number(mes) - 1, Number(dia));

  return Number.isNaN(fecha.getTime()) ? null : fecha;
}

/**
 * El camino de vuelta: el `Date` que eligió el calendario → el `2026-09-03`
 * que espera el API.
 *
 * Con los componentes locales y NUNCA con `toISOString()`, que traduce a UTC:
 * el 3 de septiembre a medianoche en Chile es el 3 a las 04:00 UTC —todavía
 * anda—, pero el 30 de septiembre a las 21:00 ya es el 1 de octubre allá.
 */
export function isoDesdeFecha(fecha: Date): string {
  const anio = String(fecha.getFullYear()).padStart(4, "0");
  const mes = String(fecha.getMonth() + 1).padStart(2, "0");
  const dia = String(fecha.getDate()).padStart(2, "0");

  return `${anio}-${mes}-${dia}`;
}
